import styled from "styled-components";

import { loadingArc } from "../../components/Button";
import { AdNicheColors } from "./niches";

// The page takes the niche's colors; the card is white, with the editor's own fields and buttons re-colored
// through their CSS variables (text typed in a field stays dark).
export const Page = styled.div<{ $colors: AdNicheColors }>`
  --ad-text: ${p => p.$colors.text};
  min-height: 100vh;
  overflow: hidden;
  background: ${p => p.$colors.background};
  color: var(--ad-text);
  font-family: "Inter", sans-serif;

  .card {
    --color-neutral-white: ${p => p.$colors.buttonText};
    --color-brand-blue: ${p => p.$colors.button};
    --color-brand-pink: ${p => p.$colors.button};
    --color-neutral-light: #8593ab;
    --color-neutral-base: #8593ab;
    --color-neutral-dark: #eceef2;
    --color-neutral-150: #434d61;
  }

  /* the button's loading: the arc and the Keepo symbol in the niche's color */
  .card [aria-busy="true"] {
    background-image: ${p => loadingArc(true, p.$colors.button)};

    &::before,
    &::after {
      background: ${p => p.$colors.button};
    }

    @media (prefers-reduced-motion: reduce) {
      background-image: ${p => loadingArc(false, p.$colors.button)};
    }
  }
`;

export const Logo = styled.div`
  svg {
    width: 96px;
    height: auto;
  }

  svg path {
    fill: var(--ad-text);
  }

  @media (min-width: 1024px) {
    position: absolute;
    top: 56px;
    left: 88px;

    svg {
      width: 131px;
    }
  }
`;

export const Layout = styled.div`
  margin: 0 auto;
  max-width: 1280px;
  padding: 24px 20px 40px;

  /* computer: the text and the card centered on the screen's height, the logo on top */
  @media (min-width: 1024px) {
    position: relative;
    display: grid;
    grid-template-columns: 1fr 640px;
    gap: 32px;
    align-items: center;
    min-height: 100vh;
    max-width: 1360px;
    padding: 112px 64px 112px 88px; /* same top and bottom: the content sits in the middle of the screen */
  }
`;

export const Title = styled.h1`
  margin-top: 40px;
  font:
    700 2.125rem/1.08 "Poppins",
    sans-serif;
  letter-spacing: -0.03em;

  @media (min-width: 1024px) {
    margin-top: 0;
    max-width: 480px;
    font-size: 3.75rem;
    line-height: 1.02;
  }
`;

export const Subtitle = styled.p`
  margin-top: 14px;
  max-width: 440px;
  font-size: 1rem;
  line-height: 1.5;
  opacity: 0.85;

  @media (min-width: 1024px) {
    margin-top: 20px;
    font-size: 1.25rem;
  }
`;

// Three numbers; the two short lines between them sit right in the middle of each gap
export const Stats = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr auto 1fr auto;
  align-items: end;
  margin-top: 28px;
  white-space: nowrap;

  &::before,
  &::after {
    grid-row: 1;
    justify-self: center;
    width: 1px;
    height: 100%;
    content: "";
    background: color-mix(in srgb, var(--ad-text) 25%, transparent);
  }

  &::before {
    grid-column: 2;
  }

  &::after {
    grid-column: 4;
  }

  div {
    display: flex;
    flex-direction: column-reverse;
    gap: 2px;
    grid-row: 1;
  }

  div:nth-of-type(1) {
    grid-column: 1;
  }

  div:nth-of-type(2) {
    grid-column: 3;
  }

  div:nth-of-type(3) {
    grid-column: 5;
  }

  dt {
    font-size: 0.75rem;
  }

  dd {
    font:
      700 1.25rem/1.1 "Poppins",
      sans-serif;
  }

  @media (min-width: 1024px) {
    grid-template-columns: auto 48px auto 48px auto;
    justify-content: start;
    margin-top: 44px;

    dt {
      font-size: 0.9375rem;
    }

    dd {
      font-size: 2rem;
    }
  }
`;

// The niche's Keepo on a phone, behind the card (or the default sign up art when the niche has no print)
/* phone: the card first, the example Keepo whole under it; computer: the phone behind the card */
export const Stage = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin-top: 32px;

  /* computer: a fixed-height stage; the card and the phone centered in it, so a taller step grows both ways */
  @media (min-width: 1024px) {
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;
    height: 660px;
    margin-top: 0;
  }
`;

export const Phone = styled.div`
  display: none; /* phone: only the text and the form */
  width: 200px;
  padding: 7px;
  border-radius: 30px;
  background: #0b0d12;

  > div {
    height: 360px;
    overflow: hidden;
    border-radius: 24px;
  }

  @media (min-width: 1024px) {
    display: block;
    position: absolute;
    top: calc(50% - 40px); /* a little above the card */
    left: -40px;
    margin: 0;
    width: 320px;
    padding: 11px;
    border-radius: 44px;
    transform: translateY(-50%);

    > div {
      height: 570px;
      border-radius: 34px;
    }
  }
`;

export const Card = styled.div`
  position: relative;
  margin: 0;
  padding: 22px;
  border-radius: 24px;
  background: #fff;
  color: #191e2a;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.18);

  /* typed text dark; not the code field's hidden input ("vi"), whose text stays invisible behind the boxes */
  input:not(.vi) {
    color: #191e2a;
  }

  /* the e-mail code boxes (white on the editor's dark sign up): the card's field look */
  .vi__character {
    border: 2px solid #b0b9c9;
    border-radius: 10px;
    color: #191e2a;
    font-weight: 700;
  }

  .vi__character--selected {
    border-color: var(--color-brand-blue);
    --tw-ring-color: transparent;
  }

  /* the field's label on focus (the editor turns it white) */
  div:focus-within + label {
    color: #191e2a;
  }

  /* the step's form box: less air inside the card */
  > div:has(> form) {
    padding: 4px 0 20px;
    border-bottom: none;
  }

  @media (min-width: 1024px) {
    width: 400px;
    margin: 0;
    padding: 32px;
    border-radius: 28px;
  }
`;

export const Steps = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;

  span {
    flex: 1;
    height: 6px;
    border-radius: 3px;
    background: #eceef2;
  }

  span.done {
    background: var(--color-brand-blue);
  }
`;

export const CardTitle = styled.h2`
  font:
    600 1.25rem/1.2 "Poppins",
    sans-serif;

  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }
`;

export const Socials = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 10px 12px;
    border-radius: 999px;
    background: #f3f4f7;
    color: #191e2a;
    font:
      600 0.875rem "Poppins",
      sans-serif;

    &:hover {
      background: #e6e9ee;
    }
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

// "ou continue com", between two short lines
export const Divider = styled.p`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px; /* same as the space above it (the form box's bottom padding) */
  color: #8593ab;
  font-size: 0.875rem;

  &::before,
  &::after {
    flex: 1;
    height: 1px;
    content: "";
    background: #e1e5ec;
  }
`;
